import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Loader } from "@/components/common";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";

import AdminDashboard from "@/pages/AdminDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import Drivers from "@/pages/Drivers";
import Vans from "@/pages/Vans";
import Stops from "@/pages/Stops";
import DpdImport from "@/pages/DpdImport";
import Earnings from "@/pages/Earnings";
import TicketFinder from "@/pages/TicketFinder";
import Tickets from "@/pages/Tickets";
import Mileage from "@/pages/Mileage";
import Fuel from "@/pages/Fuel";
import FuelBudget from "@/pages/FuelBudget";
import Expenses from "@/pages/Expenses";
import Profit from "@/pages/Profit";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import DriverStops from "@/pages/DriverStops";
import DriverFuel from "@/pages/DriverFuel";
import DriverTickets from "@/pages/DriverTickets";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader label="Authenticating" />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const Dashboard = () => {
  const { user } = useAuth();
  return user?.role === "driver" ? <DriverDashboard /> : <AdminDashboard />;
};

const STAFF = ["admin", "manager"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/drivers" element={<Protected roles={STAFF}><Drivers /></Protected>} />
      <Route path="/vans" element={<Protected roles={STAFF}><Vans /></Protected>} />
      <Route path="/stops" element={<Protected roles={STAFF}><Stops /></Protected>} />
      <Route path="/dpd-import" element={<Protected roles={STAFF}><DpdImport /></Protected>} />
      <Route path="/earnings" element={<Protected roles={STAFF}><Earnings /></Protected>} />
      <Route path="/ticket-finder" element={<Protected roles={STAFF}><TicketFinder /></Protected>} />
      <Route path="/tickets" element={<Protected roles={STAFF}><Tickets /></Protected>} />
      <Route path="/mileage" element={<Protected roles={STAFF}><Mileage /></Protected>} />
      <Route path="/fuel" element={<Protected roles={STAFF}><Fuel /></Protected>} />
      <Route path="/fuel-budget" element={<Protected roles={STAFF}><FuelBudget /></Protected>} />
      <Route path="/expenses" element={<Protected roles={STAFF}><Expenses /></Protected>} />
      <Route path="/profit" element={<Protected roles={STAFF}><Profit /></Protected>} />
      <Route path="/reports" element={<Protected roles={STAFF}><Reports /></Protected>} />
      <Route path="/audit" element={<Protected roles={STAFF}><AuditLog /></Protected>} />
      <Route path="/settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />

      <Route path="/my-stops" element={<Protected roles={["driver"]}><DriverStops /></Protected>} />
      <Route path="/my-fuel" element={<Protected roles={["driver"]}><DriverFuel /></Protected>} />
      <Route path="/my-tickets" element={<Protected roles={["driver"]}><DriverTickets /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRouter />
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
